import React, { useState, useEffect, useRef } from 'react';
import { BookOpen, Search, Download, Upload, GitBranch, List, HardDrive } from 'lucide-react';
import { StudyNote, VerseReference, Category } from './types';
import { NoteEditor } from './components/NoteEditor';
import { NoteCard } from './components/NoteCard';
import { ReferenceModal } from './components/ReferenceModal';
import { CategorySidebar } from './components/CategorySidebar';
import { CrossReferenceMap } from './components/CrossReferenceMap';
import { saveNotes, loadNotes, saveCategories, loadCategories, pickSaveFile, hasSaveFileStored } from './utils/storage';
import { exportNotesToFile, importNotesFromFile } from './utils/exportImport';
import { generateVerseId } from './utils/verseParser';

function App() {
  const [notes, setNotes] = useState<StudyNote[]>([]);
  const [categories, setCategories] = useState<Category[]>([]);
  const [selectedCategoryId, setSelectedCategoryId] = useState<string | null>(null);
  const [editingNote, setEditingNote] = useState<StudyNote | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [currentNoteId, setCurrentNoteId] = useState<string | null>(null);
  const [currentParentId, setCurrentParentId] = useState<string | null>(null);
  const [editingReference, setEditingReference] = useState<VerseReference | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [viewMode, setViewMode] = useState<'list' | 'map'>('list');
  const [hasSaveFile, setHasSaveFile] = useState(false);

  useEffect(() => {
    const init = async () => {
      const [loadedNotes, loadedCategories, hsf] = await Promise.all([loadNotes(), loadCategories(), hasSaveFileStored()]);
      setNotes(loadedNotes);
      setCategories(loadedCategories);
      setHasSaveFile(hsf);
    };
    init();
  }, []);

  useEffect(() => {
    saveNotes(notes);
  }, [notes]);

  useEffect(() => {
    saveCategories(categories);
  }, [categories]); // async calls — no await needed, fire-and-forget is fine for saves

  const handleSaveNote = (note: StudyNote) => {
    if (editingNote) {
      setNotes(notes.map(n => n.id === note.id ? note : n));
      setEditingNote(null);
    } else {
      setNotes([...notes, note]);
    }
  };

  const handleDeleteNote = (id: string) => {
    if (window.confirm('Are you sure you want to delete this note?')) {
      setNotes(notes.filter(n => n.id !== id));
    }
  };

  const handleEditNote = (note: StudyNote) => {
    setEditingNote(note);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const toggleVerseInTree = (noteId: string, verseId: string, verses: VerseReference[]): VerseReference[] => {
    return verses.map(verse => {
      if (verse.id === verseId) {
        return { ...verse, isExpanded: !verse.isExpanded };
      }
      if (verse.linkedVerses && verse.linkedVerses.length > 0) {
        return { ...verse, linkedVerses: toggleVerseInTree(noteId, verseId, verse.linkedVerses) };
      }
      return verse;
    });
  };

  const handleToggleVerse = (noteId: string, verseId: string) => {
    setNotes(notes.map(note => {
      if (note.id === noteId) {
        return { ...note, linkedVerses: toggleVerseInTree(noteId, verseId, note.linkedVerses) };
      }
      return note;
    }));
  };

  const handleAddReference = (noteId: string, parentId: string) => {
    setCurrentNoteId(noteId);
    setCurrentParentId(parentId);
    setEditingReference(null);
    setIsModalOpen(true);
  };

  const handleEditReference = (noteId: string, reference: VerseReference) => {
    setCurrentNoteId(noteId);
    setEditingReference(reference);
    setIsModalOpen(true);
  };

  const addReferenceToTree = (verses: VerseReference[], parentId: string, newRef: VerseReference): VerseReference[] => {
    return verses.map(verse => {
      if (verse.id === parentId) {
        const existingRefs = verse.linkedVerses || [];
        return { ...verse, linkedVerses: [...existingRefs, newRef] };
      }
      if (verse.linkedVerses && verse.linkedVerses.length > 0) {
        return { ...verse, linkedVerses: addReferenceToTree(verse.linkedVerses, parentId, newRef) };
      }
      return verse;
    });
  };

  const updateReferenceInTree = (verses: VerseReference[], updatedRef: VerseReference): VerseReference[] => {
    return verses.map(verse => {
      if (verse.id === updatedRef.id) {
        return { ...updatedRef, linkedVerses: verse.linkedVerses, isExpanded: verse.isExpanded };
      }
      if (verse.linkedVerses && verse.linkedVerses.length > 0) {
        return { ...verse, linkedVerses: updateReferenceInTree(verse.linkedVerses, updatedRef) };
      }
      return verse;
    });
  };

  const handleSaveReference = (reference: VerseReference) => {
    if (!currentNoteId) return;

    setNotes(notes.map(note => {
      if (note.id === currentNoteId) {
        if (editingReference) {
          return { ...note, linkedVerses: updateReferenceInTree(note.linkedVerses, reference) };
        } else if (currentParentId) {
          return { ...note, linkedVerses: addReferenceToTree(note.linkedVerses, currentParentId, reference) };
        }
      }
      return note;
    }));

    setCurrentNoteId(null);
    setCurrentParentId(null);
    setEditingReference(null);
  };

  const deleteReferenceFromTree = (verses: VerseReference[], parentId: string | null, refId: string): VerseReference[] => {
    if (parentId === null) {
      return verses.filter(v => v.id !== refId);
    }
    return verses.map(verse => {
      if (verse.id === parentId) {
        return { ...verse, linkedVerses: (verse.linkedVerses || []).filter(v => v.id !== refId) };
      }
      if (verse.linkedVerses && verse.linkedVerses.length > 0) {
        return { ...verse, linkedVerses: deleteReferenceFromTree(verse.linkedVerses, parentId, refId) };
      }
      return verse;
    });
  };

  const handleDeleteReference = (noteId: string, parentId: string | null, referenceId: string) => {
    if (!window.confirm('Delete this reference?')) return;

    setNotes(notes.map(note => {
      if (note.id === noteId) {
        return { ...note, linkedVerses: deleteReferenceFromTree(note.linkedVerses, parentId, referenceId) };
      }
      return note;
    }));
  };

  const handleExport = () => {
    exportNotesToFile(notes, categories);
  };

  const handleImportClick = () => {
    fileInputRef.current?.click();
  };

  const handleImportFile = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    try {
      const imported = await importNotesFromFile(file);
      const msg = `Import ${imported.notes.length} note(s) and ${imported.categories.length} category(s)? This will replace your current data.`;
      if (window.confirm(msg)) {
        setNotes(imported.notes);
        setCategories(imported.categories);
        alert('Imported successfully!');
      }
    } catch (error) {
      alert('Failed to import notes: ' + (error as Error).message);
    }

    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleAddCategory = (name: string) => {
    const newCategory: Category = {
      id: generateVerseId(),
      name
    };
    setCategories([...categories, newCategory]);
  };

  const handleEditCategory = (categoryId: string, name: string) => {
    setCategories(categories.map(cat => 
      cat.id === categoryId ? { ...cat, name } : cat
    ));
  };

  const handleDeleteCategory = (categoryId: string) => {
    setCategories(categories.filter(cat => cat.id !== categoryId));
    setNotes(notes.map(note => 
      note.categoryId === categoryId ? { ...note, categoryId: undefined } : note
    ));
    if (selectedCategoryId === categoryId) {
      setSelectedCategoryId(null);
    }
  };

  const handleMoveCategory = (categoryId: string, direction: 'up' | 'down') => {
    const index = categories.findIndex(cat => cat.id === categoryId);
    if (index === -1) return;
    
    if (direction === 'up' && index === 0) return;
    if (direction === 'down' && index === categories.length - 1) return;
    
    const newCategories = [...categories];
    const targetIndex = direction === 'up' ? index - 1 : index + 1;
    
    [newCategories[index], newCategories[targetIndex]] = [newCategories[targetIndex], newCategories[index]];
    
    setCategories(newCategories);
  };

  const noteCountByCategory = notes.reduce((acc, note) => {
    if (note.categoryId) {
      acc[note.categoryId] = (acc[note.categoryId] || 0) + 1;
    }
    return acc;
  }, {} as Record<string, number>);

  const filteredNotes = notes.filter(note => {
    const query = searchQuery.toLowerCase();
    const matchesSearch = (
      note.verse.display.toLowerCase().includes(query) ||
      (note.content ?? '').toLowerCase().includes(query) ||
      note.linkedVerses.some(v => v.display.toLowerCase().includes(query))
    );
    const matchesCategory = selectedCategoryId === null || note.categoryId === selectedCategoryId;
    return matchesSearch && matchesCategory;
  });

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100">
      <header className="bg-white shadow-md">
        <div className="max-w-full mx-auto px-4 sm:px-8 py-3 sm:py-6">
          <div className="flex items-center justify-between gap-2">
            <div className="flex items-center gap-2 min-w-0">
              <BookOpen className="text-blue-600 flex-shrink-0" size={24} />
              <h1 className="text-lg sm:text-3xl font-bold text-gray-800 truncate">Bible Study Notes</h1>
            </div>
            <div className="flex gap-1 sm:gap-2 flex-shrink-0">
              <button
                onClick={async () => {
                  const ok = await pickSaveFile(notes, categories);
                  if (ok) setHasSaveFile(true);
                }}
                className={`flex items-center gap-1 sm:gap-2 px-2 sm:px-4 py-2 rounded-lg transition-colors text-sm ${
                  hasSaveFile
                    ? 'bg-emerald-600 text-white hover:bg-emerald-700'
                    : 'bg-orange-500 text-white hover:bg-orange-600'
                }`}
                title={hasSaveFile ? 'Auto-saving to file — click to change save file' : 'Pick a save file for permanent storage'}
              >
                <HardDrive size={18} />
                <span className="hidden sm:inline">{hasSaveFile ? 'Saved to File' : 'Pick Save File'}</span>
              </button>
              <button
                onClick={handleExport}
                className="flex items-center gap-1 sm:gap-2 px-2 sm:px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors text-sm"
                title="Export notes to file"
              >
                <Download size={18} />
                <span className="hidden sm:inline">Export</span>
              </button>
              <button
                onClick={handleImportClick}
                className="flex items-center gap-1 sm:gap-2 px-2 sm:px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors text-sm"
                title="Import notes from file"
              >
                <Upload size={18} />
                <span className="hidden sm:inline">Import</span>
              </button>
              <input
                ref={fileInputRef}
                type="file"
                accept=".json"
                onChange={handleImportFile}
                className="hidden"
              />
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-full mx-auto px-3 sm:px-8 py-4 sm:py-8">
        <div className="flex flex-col sm:flex-row gap-4 sm:gap-6">
          <div className="w-full sm:w-64 sm:flex-shrink-0">
            <CategorySidebar
              categories={categories}
              selectedCategoryId={selectedCategoryId}
              onSelectCategory={setSelectedCategoryId}
              onAddCategory={handleAddCategory}
              onEditCategory={handleEditCategory}
              onDeleteCategory={handleDeleteCategory}
              onMoveCategory={handleMoveCategory}
              noteCountByCategory={noteCountByCategory}
            />
          </div>
          
          <div className="flex-1 min-w-0 w-full">
            <NoteEditor 
              onSave={handleSaveNote} 
              editingNote={editingNote}
              onCancel={() => setEditingNote(null)}
              categories={categories}
            />

            <div className="bg-white rounded-lg shadow-lg p-4 mb-6">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={20} />
                <input
                  type="text"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder="Search notes, verses, or linked references..."
                  className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>
            </div>

            <div className="flex items-center justify-between mb-4">
              <span className="text-sm text-gray-500">{filteredNotes.length} note{filteredNotes.length !== 1 ? 's' : ''}</span>
              <div className="flex rounded-lg border border-gray-200 overflow-hidden text-sm">
                <button
                  onClick={() => setViewMode('list')}
                  className={`flex items-center gap-2 px-4 py-2 transition-colors ${
                    viewMode === 'list' ? 'bg-blue-600 text-white' : 'bg-white text-gray-600 hover:bg-gray-50'
                  }`}
                >
                  <List size={16} /> Notes
                </button>
                <button
                  onClick={() => setViewMode('map')}
                  className={`flex items-center gap-2 px-4 py-2 transition-colors ${
                    viewMode === 'map' ? 'bg-blue-600 text-white' : 'bg-white text-gray-600 hover:bg-gray-50'
                  }`}
                >
                  <GitBranch size={16} /> Map
                </button>
              </div>
            </div>

            {viewMode === 'map' ? (
              <CrossReferenceMap notes={filteredNotes} />
            ) : (
            <div className="space-y-4">
              {filteredNotes.length === 0 ? (
                <div className="bg-white rounded-lg shadow-md p-8 text-center">
                  <BookOpen className="mx-auto mb-4 text-gray-400" size={48} />
                  <p className="text-gray-600 text-lg">
                    {notes.length === 0 
                      ? 'No study notes yet. Create your first note above!' 
                      : 'No notes match your search.'}
                  </p>
                </div>
              ) : (
                filteredNotes.map((note) => (
                  <div key={note.id}>
                    <NoteCard
                      note={note}
                      onEdit={handleEditNote}
                      onDelete={handleDeleteNote}
                      onToggleVerse={handleToggleVerse}
                      onAddReference={handleAddReference}
                      onEditReference={handleEditReference}
                      onDeleteReference={handleDeleteReference}
                    />
                  </div>
                ))
              )}
            </div>
            )}
          </div>
        </div>
      </main>

      <ReferenceModal
        isOpen={isModalOpen}
        onClose={() => {
          setIsModalOpen(false);
          setCurrentNoteId(null);
          setCurrentParentId(null);
          setEditingReference(null);
        }}
        onSave={handleSaveReference}
        editingReference={editingReference}
        title={editingReference ? 'Edit Reference' : 'Add Reference'}
      />

      <footer className="bg-white shadow-md mt-8 sm:mt-12">
        <div className="max-w-full mx-auto px-4 sm:px-8 py-4 text-center text-gray-600 text-sm">
          <p>Bible Study Notes - Track your spiritual journey with interconnected verse references</p>
        </div>
      </footer>
    </div>
  );
}

export default App;
